import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import Swal from 'sweetalert2';
import { UserService } from "../services/UserService";
import LabelMessage from './LabelMessage';
import "./ResetPassword.scss";
import "./Onboarding.scss";
import { ReactComponent as UserActivatedSVG } from "../../node_modules/itd-common/assets/images/onboarding/illus_password.svg";
import { ReactComponent as LinkExpiredSVG } from "../../node_modules/itd-common/assets/images/onboarding/illus_linkexpired.svg";
import Footer from './layout/Footer';
import SubmitButton from './controls/buttons/submitButton';
import Form from './containers/form';
import PasswordField from './controls/fields/passwordField';
import StepProgress, { PageModeOnboarding } from './layout/auth/onboardingSteps';
import Icon from './controls/icon/icon';
import { AuthAsideDefault } from './layout/auth';
import BrandLogo from '../components/layout/auth/authLogoModal';
import { ReactComponent as ExpiredPasswordResetSVG } from "../../node_modules/itd-common/assets/images/onboarding/expired_password_02.svg";


interface IProps { }
let ResetPassword: React.FC<IProps> = () => {
    let aux = "";
    const T_PREFIX: string = 'pages.resetKey.';
    const urlParams = new URLSearchParams(window.location.search);
    const isOnboardingStep: boolean = urlParams.get("onboarding") === "true";
    const isExpiredFlow: boolean = urlParams.get("expired") === "true";
    const expiredUsername: string = sessionStorage.getItem('expiredUsername') || "";
    const T_PREFIX_ONBOARDING: string = isOnboardingStep ? "onboarding." : "";

    const [pageMode, setPageMode] = useState<PageModeOnboarding>(PageModeOnboarding.RESET_PASSWORD);
    const [isPasswordChanged, setIsPasswordChanged] = useState<boolean>(false);
    const [isErrorInPasswordChanged, setIsErrorInPasswordChanged] = useState<boolean>(false);
    const { t } = useTranslation();
    const [newKey, setNewKey] = useState('');
    const [resetKey, setResetKey] = useState('');
    const [currentKey, setCurrentKey] = useState('');
    const [isValid, setIsValid] = useState(false);
    const [hasBetween8_16Chars, setHasBetween8_16Chars] = useState(false);
    const [has1Uppercase, setHas1Uppercase] = useState(false);
    const [has1Lowercase, setHas1Lowercase] = useState(false);
    const [has1Number, setHas1Number] = useState(false);
    const [has1SpecialChar, setHas1SpecialChar] = useState(false);
    const [hasDifferentConsecutiveChars, setHasDifferentConsecutiveChars] = useState(true);
    const [message, setMessage] = useState('');
    const [bgImage, setBgImage] = useState("/bgmerchantcenter.jpg");

    useEffect(() => {
        const updateBg = () => {
            setBgImage(window.innerWidth <= 425 ? "/bgmerchantcentermobile.jpg" : "/bgmerchantcenter.jpg");
        };
        updateBg();
        window.addEventListener("resize", updateBg);
        return () => window.removeEventListener("resize", updateBg);
    }, []);

    useEffect(() => {
        if (isExpiredFlow && !expiredUsername) {
            window.location.href = '/login';
        }
    }, []);

    let update = (event: React.ChangeEvent<HTMLInputElement>): void => {

        const emailRegex = /^(?!.*(.)\1{2})(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.#^])[A-Za-z\d@$!%*?&.#^]{8,16}$/;
        aux = event.target.value;
        keyValidate(aux);
        if (emailRegex.test(aux)) {
            setIsValid(true);
        } else {
            setIsValid(false);
            setMessage('please enter a valid password');
        }
    };

    let keyReset = (): void => {
        if (isValid && newKey == resetKey && (!isExpiredFlow || currentKey)) {
            const handleResponse = (response: any) => {
                sessionStorage.removeItem('expiredUsername');
                if (response.status != 200) {
                    setIsPasswordChanged(true);
                    setIsErrorInPasswordChanged(false);
                } else {
                    setIsPasswordChanged(true);
                }
            };
            const handleError = (data: any) => {
                if (data.response?.data.includes("passwordInHistory")) {
                    Swal.fire({
                        title: t('errorOcurred'),
                        html: t("passwordInHistory"),
                        confirmButtonColor: '#f60',
                        icon: "warning"
                    });
                } else if (data.response?.data.includes("currentPasswordInvalid")) {
                    Swal.fire({
                        title: t('errorOcurred'),
                        html: t(`${T_PREFIX}expired.currentPasswordInvalid`),
                        confirmButtonColor: '#f60',
                        icon: "warning"
                    });
                } else if (data.response?.data.includes("blokedUser")) {
                    Swal.fire({
                        title: t('errorOcurred'),
                        html: t(`${T_PREFIX}expired.blokedUser`),
                        confirmButtonColor: '#f60',
                        icon: "warning"
                    }).then(() => {
                        sessionStorage.removeItem('expiredUsername');
                        window.location.href = '/login';
                    });
                } else {
                    Swal.fire({
                        title: t('errorOcurred'),
                        html: t("contactSupport"),
                        confirmButtonColor: '#f60',
                        icon: "warning"
                    });
                }
            };

            if (isExpiredFlow) {
                UserService.ResetPassword(expiredUsername, currentKey, newKey)
                    .then(handleResponse)
                    .catch(handleError);
            } else {
                let token = "";
                if (window.location.href.indexOf("token=") > 0) {
                    token = new URLSearchParams(window.location.href.substring(window.location.href.indexOf("token="))).get("token") + ""
                }
                UserService.RecoverPass(newKey, token)
                    .then(handleResponse)
                    .catch(handleError);
            }
        }
    };

    let isKeyConditionValid = (condition: boolean): JSX.Element => {
        return <Icon name="info" className={`me-2 fs-18 ${condition ? 'text-success' : 'text-danger'}`} />;
    };

    return (
        <React.Fragment>
            <div className="login" >
                <div className="page">
                    <div className="container-login100" style={{ backgroundImage: `url(${bgImage})` }}>
                        <div className={`wrap-login100 p-6${isExpiredFlow ? ' wrap-login100--wide' : ''}`}>
                            <BrandLogo />
                            {
                                !isPasswordChanged ?
                                    <>
                                        {isOnboardingStep &&
                                            <StepProgress pageMode={pageMode}></StepProgress>
                                        }
                                        <h2 className="login100-form-title text-start my-2">
                                            {isExpiredFlow
                                                ? t(`${T_PREFIX}expired.title`)
                                                : t(`${T_PREFIX}${T_PREFIX_ONBOARDING}title`)
                                            }
                                        </h2>
                                        <div className='row'>
                                            <small className='mb-1'>{t(`${T_PREFIX}${T_PREFIX_ONBOARDING}description`)}</small>
                                        </div>
                                        <Form model={[resetKey, setResetKey]}>
                                            <>
                                                {isExpiredFlow && (
                                                    <div className="row">
                                                        <div className="col-md-12 mt-2">
                                                            <label>{t(`${T_PREFIX}expired.currentKey`)}</label>
                                                            <PasswordField
                                                                attr="currentKey"
                                                                value={currentKey}
                                                                label={t(`${T_PREFIX}expired.currentKeyPlaceholder`)}
                                                                onChange={(value) => setCurrentKey(value.target.value)}
                                                                required={true}
                                                                autoFocus
                                                            ></PasswordField>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="row">
                                                    <div className="col-md-12 mt-2">
                                                        <label>{isExpiredFlow ? t(`${T_PREFIX}expired.newKey`) : t(`${T_PREFIX}${T_PREFIX_ONBOARDING}key`)}</label>
                                                        <PasswordField
                                                            attr="newKey"
                                                            value={newKey}
                                                            label={t(`${T_PREFIX}${T_PREFIX_ONBOARDING}newKey`)}
                                                            onChange={(value) => { setNewKey(value.target.value); update(value) }}
                                                            required={true}
                                                            autoFocus={!isExpiredFlow}
                                                        ></PasswordField>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-12 mt-1">
                                                        <label>{isExpiredFlow ? t(`${T_PREFIX}expired.reEntryKey`) : t(`${T_PREFIX}${T_PREFIX_ONBOARDING}key`)}</label>
                                                        <PasswordField
                                                            attr="resetKey"
                                                            value={resetKey}
                                                            label={t(`${T_PREFIX}${T_PREFIX_ONBOARDING}reEntryKey`)}
                                                            onChange={(value) => { setResetKey(value.target.value) }}
                                                            required={true}
                                                        ></PasswordField>
                                                        <LabelMessage isLabelError={true} valid={newKey === resetKey} message={t(`${T_PREFIX}keyNotMatch`)} ></LabelMessage>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-12 m3-2">
                                                        {!isValid &&
                                                            <>
                                                                <div className='text mb-3'>{t(`${T_PREFIX}passwordMustInclude`)}</div>
                                                                <div className='d-flex justify-content-start align-items-center mb-1'>{isKeyConditionValid(hasBetween8_16Chars)} <small>{t(`${T_PREFIX}passwordMustHave8_16Chars`)}</small></div>
                                                                <div className='d-flex justify-content-start align-items-center mb-1'>{isKeyConditionValid(has1Uppercase)}<small>{t(`${T_PREFIX}passwordUppercase`)}</small></div>
                                                                <div className='d-flex justify-content-start align-items-center mb-1'>{isKeyConditionValid(has1Lowercase)}<small>{t(`${T_PREFIX}passwordLowercase`)}</small></div>
                                                                <div className='d-flex justify-content-start align-items-center mb-1'>{isKeyConditionValid(has1Number)}<small>{t(`${T_PREFIX}passwordOneNumber`)}</small></div>
                                                                <div className='d-flex justify-content-start align-items-center mb-1'>{isKeyConditionValid(has1SpecialChar)}<small>{t(`${T_PREFIX}passwordSpecialCharacter`)}</small></div>
                                                                <div>{isKeyConditionValid(hasDifferentConsecutiveChars)}<small>{t(`${T_PREFIX}passwordTwoCharacters`)}</small></div>
                                                            </>
                                                        }
                                                    </div>
                                                </div>
                                                <div className="row" >
                                                    <div className="col-md-12 mt-3"  >
                                                        <SubmitButton onClick={keyReset} className='sendingButton ' label={t(`${T_PREFIX}continue`)}></SubmitButton>
                                                    </div>
                                                    <div className="col-md-12 d-flex justify-content-center mt-4">
                                                        <Link to={'/help-outside'} className="linkTo">
                                                            {t(`${T_PREFIX}needHelp`)}
                                                        </Link>                                                    
                                                    </div>
                                                </div>
                                            </>
                                        </Form>
                                    </>
                                    :
                                    isErrorInPasswordChanged ?
                                        <>
                                            <div className="row">
                                                <div className="col-md-12">
                                                    <LinkExpiredSVG className='py-1 px-3 ' />
                                                </div>
                                                <div className="col-md-12 my-2">
                                                    <h6 className='fw-bold text-center'>{t(`${T_PREFIX}linkExpirated.title`)}</h6>
                                                    <small className='text-center'>{t(`${T_PREFIX}linkExpirated.description`)}</small>
                                                </div>
                                                <div className="col-md-12 mt-3"  >
                                                    <SubmitButton onClick={() => window.location.href = '/login'} className='sendingButton ' label={t(`${T_PREFIX}goToStart`)}></SubmitButton>
                                                </div>
                                            </div>
                                        </>
                                        :
                                        <>
                                            <div className="row">
                                                <div className="col-md-12 d-flex justify-content-center">
                                                    {isExpiredFlow
                                                        ? <ExpiredPasswordResetSVG className='py-1 px-3 ' />
                                                        : <UserActivatedSVG className='py-1 px-3 ' />
                                                    }
                                                </div>
                                                <div className="col-md-12 my-2">
                                                    <h6 className='fw-bold text-center'>{
                                                        isOnboardingStep
                                                            ? t(`${T_PREFIX}userActivated.newTitle`)
                                                            : t(`${T_PREFIX}userActivated.title`)
                                                    }</h6>
                                                    <small className='text-center'>{
                                                        isOnboardingStep
                                                            ? t(`${T_PREFIX}userActivated.newDescription`)
                                                            : t(`${T_PREFIX}userActivated.description`)
                                                    }</small>
                                                </div>
                                                <div className="col-md-12 mt-3"  >
                                                    <SubmitButton onClick={() => window.location.href = '/login'} className='sendingButton ' label={t(`${T_PREFIX}goToStart`)}></SubmitButton>
                                                </div>
                                            </div>
                                        </>
                            }
                        </div>
                        { !isExpiredFlow && <AuthAsideDefault /> }
                    </div>
                </div>
            </div>
            <Footer isValid={true} />
        </React.Fragment>
    )

    function keyValidate(key: string) {
        setHasBetween8_16Chars(key.length > 7 && key.length <= 16);
        setHas1Uppercase(/[A-Z]/.test(key));
        setHas1Lowercase(/[a-z]/.test(key));
        setHas1Number(/\d/.test(key));
        setHas1SpecialChar(/[@$!%*?&.#^]/.test(key));
        setHasDifferentConsecutiveChars(!(/(.)\1{1}/.test(key)));
    }
}
export default ResetPassword